import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Permission, Role } from '../models/access.model';
import { AccountStatusResponse, AssignUserToBusinessResponse, BusinessModel, CreateUserRequest, CreateUserResponse, ResetAuthenticatorResponse, ResetUserPasswordResponse, ResetUserPinResponse, UpdateUserProfileRequest, UpdateUserProfileResponse, UserPinStatus, UserSummary } from '../models/user.model';
import { UserPlanSummary } from '../models/subscription.model';
import { AssignUserPlanRequest, AssignUserPlanResponse } from '../models/plan.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UserRoleService {
  private readonly baseUrl = environment.subscriptionApiUrl + '/api';

  constructor(private http: HttpClient) { }

  getUsers(): Observable<UserSummary[]> {
    return this.http.get<UserSummary[]>(`${this.baseUrl}/UserManagement/users`);
  }

  /**
   * Create a new identity account (admin only). When the backend generates a
   * temporary password it is returned once so the admin can hand it over.
   */
  createUser(request: CreateUserRequest): Observable<CreateUserResponse> {
    return this.http.post<CreateUserResponse>(`${this.baseUrl}/UserManagement/users`, request);
  }

  getUserById(userId: string): Observable<UserSummary> {
    return this.http.get<UserSummary>(`${this.baseUrl}/user/${encodeURIComponent(userId)}`);
  }

  getRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.baseUrl}/Roles`);
  }

  /**
   * Delete a user (SuperAdmin only). Users with business/subscription/payment
   * history are refused server-side with a 409 and an explanatory message.
   */
  deleteUser(userId: string, reason: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}`, { params: { reason } });
  }

  /**
   * Switch the account's sign-in off: no sign-in by any route, no new or refreshed tokens, no
   * access to any business through the API, POS PIN refused. Not your own account; platform
   * staff only by a SuperAdmin. The reason goes to the admin audit trail.
   */
  disableUser(userId: string, reason: string | null): Observable<AccountStatusResponse> {
    return this.http.post<AccountStatusResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/disable`,
      { reason }
    );
  }

  /** Lift a disable; they sign in again with their existing password. */
  enableUser(userId: string): Observable<AccountStatusResponse> {
    return this.http.post<AccountStatusResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/enable`,
      {}
    );
  }

  /**
   * Admin edit of another user's name and phone. Distinct from Users/UpdateUser,
   * which takes the id from the caller's token and so only edits the caller.
   */
  updateUserProfile(userId: string, request: UpdateUserProfileRequest): Observable<UpdateUserProfileResponse> {
    return this.http.put<UpdateUserProfileResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/profile`,
      request
    );
  }

  /**
   * Admin override: change the user's plan (upgrade/downgrade) or start a trial.
   * Local only — no subscription is created or amended at the payment provider.
   */
  assignUserPlan(userId: string, request: AssignUserPlanRequest): Observable<AssignUserPlanResponse> {
    return this.http.put<AssignUserPlanResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/plan`,
      request
    );
  }

  /**
   * Admin override: mark the user's active plan cancelled. Local only —
   * billing at the payment provider is unaffected.
   */
  cancelUserPlan(userId: string): Observable<{ cancelled: number }> {
    return this.http.post<{ cancelled: number }>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/plan/cancel`,
      {}
    );
  }

  /**
   * Cancel one specific plan row — for resolving a user who holds more than one
   * active plan, where "the active plan" is ambiguous. Local only.
   */
  cancelUserPlanById(userId: string, userPlanId: string): Observable<{ cancelled: number }> {
    return this.http.post<{ cancelled: number }>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/plans/${encodeURIComponent(userPlanId)}/cancel`,
      {}
    );
  }

  /** Every plan record for a user (any status), newest first. */
  getUserPlans(userId: string): Observable<UserPlanSummary[]> {
    return this.http.get<UserPlanSummary[]>(`${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/plans`);
  }

  /**
   * The POS PIN(s) this user holds and what each one unlocks. Platform staff have a single
   * PIN valid everywhere; a merchant user has the shared PIN plus one per business that
   * requires its own. Readable by any admin.
   */
  getUserPinStatus(userId: string): Observable<UserPinStatus> {
    return this.http.get<UserPinStatus>(`${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/pin`);
  }

  /**
   * Set a new random PIN for a user and return it once, so it can be handed over. The old
   * PIN stops working immediately and any lockout is cleared. `businessId` names which PIN
   * when the user holds more than one. Support may do this for merchant users; a platform
   * staff PIN is SuperAdmin-only and answers 403.
   */
  resetUserPin(userId: string, businessId?: string | null): Observable<ResetUserPinResponse> {
    return this.http.post<ResetUserPinResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/pin/reset`,
      { businessId: businessId || null }
    );
  }

  /**
   * Email the user a single-use link to choose their own PIN, so nobody else ever sees it.
   * The existing PIN keeps working until they follow the link. Same audience as resetUserPin.
   */
  sendUserPinSetupEmail(userId: string, businessId?: string | null): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/pin/send-setup-email`,
      { businessId: businessId || null }
    );
  }

  /**
   * Replace a user's sign-in password with a generated temporary one, returned once so it can
   * be handed over — the existing password is only ever stored hashed and cannot be shown.
   * Also clears any sign-in lockout. Support may do this for merchant users; platform staff
   * accounts are SuperAdmin-only and answer 403, and nobody may reset their own here.
   */
  resetUserPassword(userId: string): Observable<ResetUserPasswordResponse> {
    return this.http.post<ResetUserPasswordResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/password/reset`,
      {}
    );
  }

  /**
   * Remove a staff member's authenticator app and recovery codes and end every session and
   * refresh token they hold; their next sign-in sets it up again. SuperAdmin-only server-side
   * (403 otherwise), never your own account (400). The reason goes to the admin audit trail.
   */
  resetAuthenticator(userId: string, reason: string | null): Observable<ResetAuthenticatorResponse> {
    return this.http.post<ResetAuthenticatorResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/authenticator/reset`,
      { reason }
    );
  }

  getSystemRolesPermissions(userId: string): Observable<{ role: Role[]; permissions: Permission[] }> {
    return this.http.get<{ role: Role[]; permissions: Permission[] }>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/systemRolesPermissions`);
  }

  addRoleToUser(userId: string, roleName: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/userrole`, { roleName });
  }

  /**
   * Remove a system role from a user. SuperAdmin/Support roles are SuperAdmin-only
   * server-side (403), and removing your own SuperAdmin role is refused.
   */
  removeRoleFromUser(userId: string, roleName: string, reason: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/userrole/${encodeURIComponent(roleName)}`, { params: { reason } }
    );
  }

  getUserBusinessess(userId: string): Observable<BusinessModel[]> {
    return this.http.get<BusinessModel[]>(`${this.baseUrl}/UserManagement/${userId}/userBusinessess`);
  }

  removeUserFromBusiness(userId: string, businessId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/businesses/${encodeURIComponent(businessId)}`);
  }

  /**
   * Assign a WebsiteBuilder to a business: a membership row with no business role, which is what
   * scopes the builder's Sites.* access. Never use addUserToBusiness for this - its default role
   * would make the builder the owner.
   */
  assignWebsiteBuilder(userId: string, businessId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/businesses/${encodeURIComponent(businessId)}`,
      {}
    );
  }

  /**
   * Link the user to a business with one business role. BusinessOwner (the default) makes them
   * its owner and is refused while someone else owns it; any other business role links them as
   * staff. Their system roles are left alone, so a seller stays a seller.
   */
  addUserToBusiness(userId: string, businessId: string, roleName: string = 'BusinessOwner'): Observable<AssignUserToBusinessResponse> {
    return this.http.post<AssignUserToBusinessResponse>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/businesses/${encodeURIComponent(businessId)}`,
      { roleName }
    );
  }

  /**
   * Retrieve active businesses available in the system.
   */
  // Moved to BusinessService

  /**
   * Retrieve all businesses available in the system.
   */
  // Moved to BusinessService

  /** Business-centric endpoints */
  // Moved to BusinessService: getBusinessDetails, getBusinessUsers, getBusinessRolesPermissions

  // Fetch roles and permissions for a specific business for a user
  getUserBusinessAccess(userId: string, businessId: string): Observable<{ roles: Role[]; userPermissions: Permission[] }> {
    return this.http.get<{ roles: Role[]; userPermissions: Permission[] }>(
      `${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/businessProfile/${encodeURIComponent(businessId)}`
    );
  }

  /**
   * Assign a role to a user within a specific business using roleName.
   */
  addRoleToUserInBusiness(userId: string, businessId: string, roleName: string): Observable<void> {
    // Payload uses roleName per request; adjust path if backend differs
    return this.http.post<void>(`${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/businesses/${encodeURIComponent(businessId)}/addroles`, { roleName });
  }

  /**
   * Remove a role from a user within a specific business using roleName.
   */
  removeRoleFromUserInBusiness(userId: string, businessId: string, roleName: string): Observable<void> {
    // Using roleName; if backend prefers body with DELETE, switch accordingly
    return this.http.post<void>(`${this.baseUrl}/UserManagement/${encodeURIComponent(userId)}/businesses/${encodeURIComponent(businessId)}/removeroles`, { roleName});
  }
}
